import Button from 'components/Forms/Button/Button';
import Spinbox from 'components/Forms/Spinbox/Spinbox';
import { mapAddToCartPopupData } from 'connectors/cart/Cart';
import { useAddToCart } from 'hooks/cart/useAddToCart';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { createPersooEventForAddToCart } from 'modules/persoo/persooEvents';
import useTranslation from 'next-translate/useTranslation';
import { MenuBasketIcon, SpinnerIcon } from 'public/svg/IconsSvg';
import { useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmListNameType } from 'types/gtm';
import { ProductActionType } from 'types/product';

type SpinboxAddToCartProps = {
    spinboxIdSuffix?: string;
    product: ProductActionType;
    gtmListName: GtmListNameType;
    listIndex: number;
    variant: 'iconOnly' | 'withText';
};

export const SpinboxAddToCart: FC<SpinboxAddToCartProps> = ({
    spinboxIdSuffix,
    product,
    gtmListName,
    listIndex,
    variant,
}) => {
    const { t } = useTranslation();
    const [changeCartItemQuantity, isAddToCartPending] = useAddToCart(gtmListName);
    const { currencyCode } = useDomainConfig();
    const { updateUserState } = usePersistStore((s) => s);
    const [quantity, setQuantity] = useState(product.b2bMinimumProductOrder ?? 1);

    const onAddToCartHandler = async () => {
        const addToCartResult = await changeCartItemQuantity(product.uuid, listIndex, quantity, gtmListName);
        const mappedAddToCartResult = mapAddToCartPopupData(addToCartResult, currencyCode);

        if (mappedAddToCartResult) {
            createPersooEventForAddToCart(mappedAddToCartResult.catalogNumber);
            updateUserState({
                addToCartPopupData: {
                    gtmListName,
                    listIndex,
                    product: mappedAddToCartResult,
                },
            });
        }
    };

    return (
        <div className="flex justify-start gap-x-1">
            <Spinbox
                id={product.uuid}
                idSuffix={spinboxIdSuffix}
                size="small"
                min={product.b2bMinimumProductOrder ?? 1}
                step={product.b2bMinimumProductOrder ?? 1}
                defaultValue={product.b2bMinimumProductOrder ?? 1}
                multiple={product.b2bMinimumProductOrder ?? 1}
                max={product.maximumOrderableQuantity}
                overstepMessage={{
                    tooHigh: t('You cannot add more items'),
                    onlyMultiplesOf: t('Only multiples of {{ multiple }}', {
                        multiple: product.b2bMinimumProductOrder ?? 1,
                    }),
                }}
                onChangeValueCallback={(newQuantityValue) => setQuantity(newQuantityValue)}
            />
            <Button
                aria-label={t('Add to cart')}
                isDisabled={
                    product.isSellingDenied ||
                    product.maximumOrderableQuantity <= 0 ||
                    (product.b2bMinimumProductOrder ?? 0) > product.maximumOrderableQuantity
                }
                className="items-center justify-center rounded-md pl-3.5 pr-0.5"
                onClick={onAddToCartHandler}
                type="button"
                variant="primary"
            >
                {isAddToCartPending ? (
                    <SpinnerIcon className="h-[26px] w-[26px]" />
                ) : (
                    <MenuBasketIcon className="h-6 w-6 stroke-white" />
                )}
                {variant === 'withText' && <span className="whitespace-nowrap pr-2 text-h5">{t('Add to cart')}</span>}
            </Button>
        </div>
    );
};
